import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  adminDocumentStatus,
  adminRegistrationStatus,
  routes,
  type AdminDocumentStatus,
  type AdminRegistrationStatus,
} from '@gg/shared';
import {
  Badge,
  Button,
  buttonClasses,
  Card,
  SelectField,
  Table,
  Td,
  TextField,
  Th,
} from '@/components/ui';
import { config } from '@/lib/config';
import { formatDateTime } from '@/lib/dates';
import { formatPrice } from '@/lib/i18n';
import { documentStatusTone, registrationStatusTone } from '@/lib/adminTones';
import { adminQueryString, useAdminRegistrations } from '@/lib/queries';

const PAGE_SIZE = 50;

/** A-03: registrations list with search, filters, counts vs capacity, and the A-06 CSV export. */
export function AdminRegistrations() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  // Filters live in the URL: reload and sharing keep the view. Payment defaults to "paid".
  const statusParam = params.get('status') ?? 'paid';
  const filters = {
    q: params.get('q') ?? undefined,
    formatId: params.get('formatId') ?? undefined,
    status: statusParam === 'all' ? undefined : (statusParam as AdminRegistrationStatus),
    profile: (params.get('profile') as 'complete' | 'incomplete' | null) ?? undefined,
    documents: (params.get('documents') as AdminDocumentStatus | null) ?? undefined,
    insured: (params.get('insured') as 'yes' | 'no' | null) ?? undefined,
  };
  const page = Math.max(1, Number(params.get('page')) || 1);

  const setFilter = (key: string, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== 'page') next.delete('page');
        return next;
      },
      { replace: true },
    );

  // The search box types freely and updates the URL after a pause.
  const [q, setQ] = useState(filters.q ?? '');
  useEffect(() => {
    const id = setTimeout(() => {
      if (q !== (params.get('q') ?? '')) setFilter('q', q.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const list = useAdminRegistrations({ ...filters, page, pageSize: PAGE_SIZE });
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.pageSize)) : 1;
  const exportHref = `${config.apiUrl}${routes.adminExport}?${adminQueryString(filters)}`;

  return (
    <section className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <h1 className="font-display text-section-lg font-bold">{t('admin.registrations.title')}</h1>
        <a href={exportHref} className={`${buttonClasses('secondary', 'md')} w-auto!`}>
          {t('admin.export')}
        </a>
      </div>

      {list.data && (
        <Card>
          <h2 className="text-sm font-bold">{t('admin.capacity.title')}</h2>
          <ul className="grid gap-x-8 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-4">
            {list.data.formats.map((f) => (
              <li key={f.formatId} className="flex justify-between gap-3">
                <span>{f.name}</span>
                <span className="font-semibold">
                  {f.capacity === null
                    ? t('admin.capacity.unlimited', { paid: f.paid })
                    : t('admin.capacity.used', { paid: f.paid, capacity: f.capacity })}
                  {f.insured > 0 && (
                    <span className="ml-2 font-normal text-ink-muted">
                      {t('admin.capacity.insured', { count: f.insured })}
                    </span>
                  )}
                  {f.held > 0 && (
                    <span className="ml-2 font-normal text-ink-muted">
                      {t('admin.capacity.held', { count: f.held })}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <TextField
          label={t('admin.search')}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <SelectField
          label={t('admin.filter.format')}
          value={filters.formatId ?? ''}
          onChange={(e) => setFilter('formatId', e.target.value)}
        >
          <option value="">{t('admin.filter.all')}</option>
          {list.data?.formats.map((f) => (
            <option key={f.formatId} value={f.formatId}>
              {f.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('admin.filter.status')}
          value={statusParam}
          onChange={(e) => setFilter('status', e.target.value)}
        >
          <option value="all">{t('admin.filter.all')}</option>
          {adminRegistrationStatus.options.map((s) => (
            <option key={s} value={s}>
              {t(`admin.status.${s}`)}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('admin.filter.profile')}
          value={filters.profile ?? ''}
          onChange={(e) => setFilter('profile', e.target.value)}
        >
          <option value="">{t('admin.filter.all')}</option>
          <option value="complete">{t('admin.profile.complete')}</option>
          <option value="incomplete">{t('admin.profile.incomplete')}</option>
        </SelectField>
        <SelectField
          label={t('admin.filter.documents')}
          value={filters.documents ?? ''}
          onChange={(e) => setFilter('documents', e.target.value)}
        >
          <option value="">{t('admin.filter.all')}</option>
          {adminDocumentStatus.options.map((s) => (
            <option key={s} value={s}>
              {t(`admin.documents.${s}`)}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('admin.filter.insured')}
          value={filters.insured ?? ''}
          onChange={(e) => setFilter('insured', e.target.value)}
        >
          <option value="">{t('admin.filter.all')}</option>
          <option value="yes">{t('admin.insured.yes')}</option>
          <option value="no">{t('admin.insured.no')}</option>
        </SelectField>
      </div>

      {list.isError && (
        <p role="alert" className="text-sm text-danger">
          {t('admin.registrations.error')}
        </p>
      )}

      {list.data && (
        <>
          <p className="text-sm text-ink-muted">
            {t('admin.registrations.count', { count: list.data.total })}
          </p>
          {list.data.rows.length === 0 ? (
            <p className="py-8 text-center text-ink-muted">{t('admin.registrations.empty')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('admin.col.date')}</Th>
                  <Th>{t('admin.col.name')}</Th>
                  <Th>{t('admin.col.email')}</Th>
                  <Th>{t('admin.col.format')}</Th>
                  <Th>{t('admin.col.status')}</Th>
                  <Th>{t('admin.col.profile')}</Th>
                  <Th>{t('admin.col.documents')}</Th>
                  <Th>{t('admin.col.insured')}</Th>
                  <Th className="text-right">{t('admin.col.price')}</Th>
                </tr>
              </thead>
              <tbody>
                {list.data.rows.map((r) => (
                  <tr key={r.id}>
                    <Td className="whitespace-nowrap text-ink-muted">
                      {formatDateTime(r.createdAt)}
                    </Td>
                    <Td>
                      <Link to={`/admin/registrations/${r.id}`} className="font-semibold">
                        {[r.user.firstName, r.user.lastName].filter(Boolean).join(' ') ||
                          t('admin.unnamed')}
                      </Link>
                    </Td>
                    <Td>{r.user.email}</Td>
                    <Td>{r.format.name}</Td>
                    <Td>
                      <Badge tone={registrationStatusTone(r.status)}>
                        {t(`admin.status.${r.status}`)}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge tone={r.profileComplete ? 'success' : 'neutral'}>
                        {t(
                          r.profileComplete ? 'admin.profile.complete' : 'admin.profile.incomplete',
                        )}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge tone={documentStatusTone(r.documentStatus)}>
                        {t(`admin.documents.${r.documentStatus}`)}
                      </Badge>
                    </Td>
                    <Td>
                      {r.insured ? (
                        <Badge tone="success">{t('admin.insured.yes')}</Badge>
                      ) : (
                        <span className="text-ink-subtle">{t('admin.none')}</span>
                      )}
                    </Td>
                    <Td className="text-right font-semibold">{formatPrice(r.priceCents)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-center gap-4 text-sm">
              <Button
                variant="secondary"
                size="md"
                className="w-auto!"
                disabled={page <= 1}
                onClick={() => setFilter('page', String(page - 1))}
              >
                {t('admin.prev')}
              </Button>
              <span>{t('admin.page', { page, pages })}</span>
              <Button
                variant="secondary"
                size="md"
                className="w-auto!"
                disabled={page >= pages}
                onClick={() => setFilter('page', String(page + 1))}
              >
                {t('admin.next')}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
